import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getCheckpointData, resumeCheckpoint } from "../utils/api";
import { dt } from "../assets/styles/theme";
import "./CheckpointSteering.css";

// ─── Design constants ────────────────────────────────────────────────────────
const PRESETS = {
  right:  { x: 0.744, y: 0.037, w: 0.224, h: 0.926 },
  bottom: { x: 0.04,  y: 0.80,  w: 0.928, h: 0.163 },
};

const HANDLES = ['nw','n','ne','e','se','s','sw','w'];
const HANDLE_CURSORS = {
  nw: 'nwse-resize', n: 'ns-resize', ne: 'nesw-resize', e: 'ew-resize',
  se: 'nwse-resize', s: 'ns-resize', sw: 'nesw-resize', w: 'ew-resize',
};

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

function regionFromBbox(bbox, imgW, imgH) {
  if (!bbox || !imgW || !imgH) return null;
  return {
    x: clamp(bbox.x / imgW, 0, 1),
    y: clamp(bbox.y / imgH, 0, 1),
    w: clamp(bbox.w / imgW, 0, 1),
    h: clamp(bbox.h / imgH, 0, 1),
  };
}

function bboxFromRegion(region, imgW, imgH) {
  return {
    x: Math.round(region.x * imgW),
    y: Math.round(region.y * imgH),
    w: Math.round(region.w * imgW),
    h: Math.round(region.h * imgH),
  };
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StepBox({ num, label, state }) {
  // state: 'done' | 'active' | 'idle'
  const bg = state === 'done' ? '#13241d' : state === 'active' ? '#112b2b' : '#151d2b';
  const numColor = state === 'done' ? dt.green : state === 'active' ? dt.teal : '#5f7398';
  const labelColor = state === 'done' ? dt.greenText : state === 'active' ? '#eaf0fb' : '#5f7398';
  return (
    <div className="cs-step-box" style={{ background: bg }}>
      <div className="cs-step-num" style={{ color: numColor, borderColor: numColor }}>
        {state === 'done' ? '✓' : num}
      </div>
      <div className="cs-step-label" style={{ color: labelColor }}>{label}</div>
    </div>
  );
}

function Stepper({ view }) {
  function stateFor(step) {
    const order = ['d1', 'd2'];
    const activeIdx = view === 'complete' ? 2 : order.indexOf(view);
    if (step - 1 < activeIdx) return 'done';
    if (step - 1 === activeIdx) return 'active';
    return 'idle';
  }
  return (
    <div className="cs-stepper">
      <StepBox num="1" label="Boundary" state={stateFor(1)} />
      <StepBox num="2" label="Panel"    state={stateFor(2)} />
    </div>
  );
}

function ReadoutCard({ label, value, color }) {
  return (
    <div className="cs-readout-card">
      <div className="cs-readout-value" style={{ color: color || dt.teal }}>{value}</div>
      <div className="cs-readout-label">{label}</div>
    </div>
  );
}

function PrimaryBtn({ onClick, children, disabled }) {
  return (
    <button className="cs-btn-primary" onClick={onClick} disabled={disabled}>
      {children} <span className="cs-btn-arrow">→</span>
    </button>
  );
}

function SecondaryBtn({ onClick, children, amber }) {
  return (
    <button className={`cs-btn-secondary${amber ? ' cs-btn-secondary--amber' : ''}`} onClick={onClick}>
      {children}
    </button>
  );
}

// Drag handle (8-point resize/move system)
function DragHandle({ dir, onPointerDown, color }) {
  const pos = {
    nw: { top: 0, left: 0 }, n: { top: 0, left: '50%' }, ne: { top: 0, right: 0 },
    e:  { top: '50%', right: 0 }, se: { bottom: 0, right: 0 },
    s:  { bottom: 0, left: '50%' }, sw: { bottom: 0, left: 0 }, w: { top: '50%', left: 0 },
  }[dir] || {};
  const translate = (dir === 'n' || dir === 's') ? 'translateX(-50%)'
    : (dir === 'e' || dir === 'w') ? 'translateY(-50%)'
    : (dir === 'nw' || dir === 'ne') ? 'none'
    : 'none';
  return (
    <div
      className="cs-drag-handle"
      style={{
        position: 'absolute',
        ...pos,
        cursor: HANDLE_CURSORS[dir],
        background: color || dt.teal,
        transform: translate,
      }}
      onPointerDown={(e) => { e.stopPropagation(); onPointerDown(e, dir); }}
    />
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function CheckpointSteering({ drawing, imageBlobUrl, onComplete }) {
  const navigate = useNavigate();

  // State machine view
  const [view, setView] = useState('d1');

  // Checkpoint data (fetched from API)
  const [checkpointData, setCheckpointData] = useState(null);
  const [loadError, setLoadError] = useState(null);

  // D1: boundary crop (fractions of sheet)
  const [crop, setCrop] = useState(null);
  const [detectedCrop, setDetectedCrop] = useState(null);

  // D2: panel exclusion region (fractions of sheet)
  const [panelRegion, setPanelRegion] = useState(null);
  const [detectedPanelRegion, setDetectedPanelRegion] = useState(null);
  const [panelEdited, setPanelEdited] = useState(false);
  const [activePreset, setActivePreset] = useState(null); // 'right' | 'bottom' | null
  const [usePanelExclusion, setUsePanelExclusion] = useState(true);

  // Sheet DOM ref for pointer math
  const sheetRef = useRef(null);
  const dragRef = useRef(null); // { field, type, dir, startFrac, startRegion }

  // Image dimensions from checkpointData
  const imgW = checkpointData?.image_shape?.[1] || 1;
  const imgH = checkpointData?.image_shape?.[0] || 1;

  // ── Fetch checkpoint data ──────────────────────────────────────────────────
  useEffect(() => {
    if (!drawing?._id) return;
    getCheckpointData(drawing._id)
      .then(res => {
        const cd = res.data;
        setCheckpointData(cd);

        const H = cd.image_shape?.[0] || 1;
        const W = cd.image_shape?.[1] || 1;

        // D1 boundary
        if (cd.boundary_bbox) {
          const r = regionFromBbox(cd.boundary_bbox, W, H);
          setCrop(r);
          setDetectedCrop(r);
        } else {
          // No boundary detected — use full sheet
          const full = { x: 0, y: 0, w: 1, h: 1 };
          setCrop(full);
          setDetectedCrop(full);
        }

        // D2 panel
        if (cd.panel_region) {
          const r = regionFromBbox(cd.panel_region, W, H);
          setPanelRegion(r);
          setDetectedPanelRegion(r);
          // Guess which preset it matches
          const rightDiff = Math.abs(r.x - PRESETS.right.x) + Math.abs(r.w - PRESETS.right.w);
          const bottomDiff = Math.abs(r.y - PRESETS.bottom.y) + Math.abs(r.h - PRESETS.bottom.h);
          setActivePreset(rightDiff < bottomDiff ? 'right' : 'bottom');
        } else {
          // Default to right preset
          setPanelRegion(PRESETS.right);
          setDetectedPanelRegion(PRESETS.right);
          setActivePreset('right');
        }
      })
      .catch(err => {
        console.error('Failed to load checkpoint data:', err);
        setLoadError('Failed to load checkpoint data. Please refresh.');
      });
  }, [drawing?._id]);

  // ── Drag engine ────────────────────────────────────────────────────────────
  const getSheetFrac = useCallback((clientX, clientY) => {
    if (!sheetRef.current) return { fx: 0, fy: 0 };
    const rect = sheetRef.current.getBoundingClientRect();
    return {
      fx: clamp((clientX - rect.left) / rect.width, 0, 1),
      fy: clamp((clientY - rect.top) / rect.height, 0, 1),
    };
  }, []);

  const startDrag = useCallback((e, field, type, dir) => {
    e.preventDefault();
    const region = field === 'crop' ? crop : panelRegion;
    const { fx, fy } = getSheetFrac(e.clientX, e.clientY);
    dragRef.current = { field, type, dir, startFx: fx, startFy: fy, startRegion: { ...region } };
    if (field === 'panel') setPanelEdited(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [crop, panelRegion, getSheetFrac]);

  const MIN = { crop: 0.12, panel: 0.05 };

  const onPointerMove = useCallback((e) => {
    if (!dragRef.current) return;
    const { field, type, dir, startFx, startFy, startRegion } = dragRef.current;
    const { fx, fy } = getSheetFrac(e.clientX, e.clientY);
    const dx = fx - startFx;
    const dy = fy - startFy;
    const minSize = MIN[field];
    let r = { ...startRegion };

    if (type === 'move') {
      r.x = clamp(startRegion.x + dx, 0, 1 - startRegion.w);
      r.y = clamp(startRegion.y + dy, 0, 1 - startRegion.h);
    } else {
      // resize by direction
      if (dir.includes('w')) {
        const newX = clamp(startRegion.x + dx, 0, startRegion.x + startRegion.w - minSize);
        r.w = startRegion.x + startRegion.w - newX;
        r.x = newX;
      }
      if (dir.includes('e')) {
        r.w = clamp(startRegion.w + dx, minSize, 1 - startRegion.x);
      }
      if (dir.includes('n')) {
        const newY = clamp(startRegion.y + dy, 0, startRegion.y + startRegion.h - minSize);
        r.h = startRegion.y + startRegion.h - newY;
        r.y = newY;
      }
      if (dir.includes('s')) {
        r.h = clamp(startRegion.h + dy, minSize, 1 - startRegion.y);
      }
    }

    if (field === 'crop') setCrop(r);
    else setPanelRegion(r);
  }, [getSheetFrac]);

  const onPointerUp = useCallback(() => { dragRef.current = null; }, []);

  useEffect(() => {
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  // ── D2 preset ─────────────────────────────────────────────────────────────
  const applyPreset = (side) => {
    setPanelRegion(PRESETS[side]);
    setActivePreset(side);
    setPanelEdited(false);
  };

  // ── Submit (build the graph) ───────────────────────────────────────────────
  const handleBuildGraph = async () => {
    setView('complete');
    try {
      const adjustments = {
        boundary_bbox: crop ? bboxFromRegion(crop, imgW, imgH) : null,
        panel_region: (usePanelExclusion && panelRegion) ? bboxFromRegion(panelRegion, imgW, imgH) : null,
        use_panel_exclusion: usePanelExclusion,
      };
      await resumeCheckpoint(drawing._id, adjustments);
      onComplete();
    } catch (err) {
      console.error('Resume failed:', err);
      setView('d2');
    }
  };

  // ── Overlay helpers ────────────────────────────────────────────────────────
  function regionStyle(r) {
    if (!r) return {};
    return { left: `${r.x * 100}%`, top: `${r.y * 100}%`, width: `${r.w * 100}%`, height: `${r.h * 100}%` };
  }

  function cropSizeLabel() {
    if (!crop) return '';
    const pw = Math.round(crop.w * imgW);
    const ph = Math.round(crop.h * imgH);
    return `${pw.toLocaleString()} × ${ph.toLocaleString()} px`;
  }

  function ofSheetLabel() {
    if (!crop) return '';
    return `${Math.round(crop.w * crop.h * 100)}% area`;
  }

  // ── Checkpoint overlay rendering ───────────────────────────────────────────
  function renderOverlay() {
    if (!checkpointData) return null;

    if ((view === 'd1') && crop) {
      return (
        <div className="cs-crop-overlay" style={regionStyle(crop)}
          onPointerDown={(e) => startDrag(e, 'crop', 'move', null)}>
          <div className="cs-crop-label">
            <span className="cs-chip cs-chip--teal">DRAWING FRAME</span>
            <span className="cs-chip cs-chip--teal cs-chip--mono">{cropSizeLabel()}</span>
          </div>
          {HANDLES.map(dir => (
            <DragHandle key={dir} dir={dir} color={dt.teal}
              onPointerDown={(e, d) => startDrag(e, 'crop', 'resize', d)} />
          ))}
          <div className="cs-hint-pill">↔ drag any handle to adjust the crop</div>
        </div>
      );
    }

    if (view === 'd2' && panelRegion && usePanelExclusion) {
      return (
        <div className="cs-panel-overlay" style={regionStyle(panelRegion)}
          onPointerDown={(e) => startDrag(e, 'panel', 'move', null)}>
          <div className="cs-panel-label">
            <span className="cs-chip cs-chip--amber">NOTES / TITLE · IGNORED</span>
          </div>
          {HANDLES.map(dir => (
            <DragHandle key={dir} dir={dir} color={dt.amber}
              onPointerDown={(e, d) => startDrag(e, 'panel', 'resize', d)} />
          ))}
          <div className="cs-hint-pill cs-hint-pill--amber">↔ drag the box to fit the panel exactly</div>
        </div>
      );
    }

    return null;
  }

  // ── Drawer body rendering ──────────────────────────────────────────────────
  function renderDrawerBody() {
    if (loadError) {
      return <div className="cs-error">{loadError}</div>;
    }
    if (!checkpointData) {
      return <div className="cs-loading"><span className="cs-spinner" /> Loading checkpoint data…</div>;
    }

    // D1 — Boundary
    if (view === 'd1') {
      return (
        <>
          <div className="cs-preview-row">
            <div className="cs-preview-tile">
              {crop && <div className="cs-preview-crop" style={{
                left: `${crop.x * 100}%`, top: `${crop.y * 100}%`,
                width: `${crop.w * 100}%`, height: `${crop.h * 100}%`
              }} />}
            </div>
            <p className="cs-caption">Detected after <strong>frame boundary detection</strong>. Everything outside this rectangle is ignored.</p>
          </div>
          <h3 className="cs-section-heading">Is this the correct drawing area?</h3>
          <p className="cs-body-text">Drag the handles on the drawing to adjust. Anything outside the teal frame will be masked before symbol detection runs.</p>
          <div className="cs-readout-row">
            <ReadoutCard label="CROP SIZE" value={cropSizeLabel()} />
            <ReadoutCard label="OF SHEET" value={ofSheetLabel()} />
          </div>
          <PrimaryBtn onClick={() => setView('d2')}>Looks right — continue</PrimaryBtn>
          <SecondaryBtn onClick={() => { setCrop(detectedCrop); }}>Reset to detected</SecondaryBtn>
        </>
      );
    }

    // D2 — Panel exclusion
    if (view === 'd2') {
      return (
        <>
          <div className="cs-preview-row">
            <div className="cs-preview-tile cs-preview-tile--panel">
              {panelRegion && usePanelExclusion && (
                <div className="cs-preview-panel" style={{
                  left: `${panelRegion.x * 100}%`, top: `${panelRegion.y * 100}%`,
                  width: `${panelRegion.w * 100}%`, height: `${panelRegion.h * 100}%`
                }} />
              )}
            </div>
            <p className="cs-caption">Detected after <strong>panel divider detection</strong>. This region looks like notes, not process.</p>
          </div>
          <h3 className="cs-section-heading">Is this the notes / title panel?</h3>
          <p className="cs-body-text">We'll <strong>ignore the shaded area</strong> so table text doesn't get misread as equipment.</p>

          {/* Presets */}
          <div className="cs-preset-row">
            <span className="cs-preset-label">Start from a preset</span>
            <div className="cs-preset-buttons">
              <button
                className={`cs-preset-btn${activePreset === 'right' && !panelEdited ? ' cs-preset-btn--active' : ''}`}
                onClick={() => applyPreset('right')}>RIGHT</button>
              <button
                className={`cs-preset-btn${activePreset === 'bottom' && !panelEdited ? ' cs-preset-btn--active' : ''}`}
                onClick={() => applyPreset('bottom')}>BOTTOM</button>
            </div>
          </div>
          <p className="cs-helper-text">↔ Neither fits? Drag the box or its handles on the drawing.</p>

          <PrimaryBtn onClick={() => { setUsePanelExclusion(true); handleBuildGraph(); }}>
            Yes — ignore this area
          </PrimaryBtn>
          <SecondaryBtn onClick={() => { setUsePanelExclusion(false); handleBuildGraph(); }}>
            No — don't exclude anything
          </SecondaryBtn>
        </>
      );
    }

    // Complete
    if (view === 'complete') {
      return (
        <div className="cs-complete">
          <div className="cs-complete-check">✓</div>
          <h3 className="cs-section-heading">All checkpoints cleared</h3>
          <p className="cs-body-text">Your input is locked in. The pipeline is finishing the remaining stages…</p>
          <div className="cs-status-list">
            <div className="cs-status-item">
              <span className="cs-dot cs-dot--green" /> Line / pipe detection <strong>running</strong>
            </div>
            <div className="cs-status-item">
              <span className="cs-dot cs-dot--amber cs-dot--pulse" /> Entity association → graph <strong>queued</strong>
            </div>
          </div>
          <div className="cs-indet-bar cs-indet-bar--teal"><div className="cs-indet-fill cs-indet-fill--teal" /></div>
        </div>
      );
    }

    return null;
  }

  // ── View labels ────────────────────────────────────────────────────────────
  const VIEW_LABELS = {
    d1: { headline: 'Confirm the drawing frame', step: 'Checkpoint 1 of 2' },
    d2: { headline: 'Confirm the ignored area',  step: 'Checkpoint 2 of 2' },
    complete: { headline: 'Steering complete', step: '2 of 2 cleared' },
  };
  const { headline, step } = VIEW_LABELS[view] || VIEW_LABELS.d1;
  const isComplete = view === 'complete';

  return (
    <div className="cs-root">
      {/* ── Top nav ── */}
      <nav className="cs-nav">
        <div className="cs-nav-left">
          <button className="cs-back-btn" onClick={() => navigate('/dashboard')}>‹</button>
          <span className="cs-brand">PANDID</span>
          <span className="cs-nav-divider" />
          <span className="cs-nav-title">{drawing?.name || 'Drawing'}</span>
        </div>
        <div className="cs-nav-right">
          {!isComplete && (
            <span className="cs-status-pill">
              <span className="cs-dot cs-dot--amber cs-dot--pulse" />
              PROCESSING · PAUSED
            </span>
          )}
          {isComplete && (
            <span className="cs-status-pill cs-status-pill--green">
              <span className="cs-dot cs-dot--green" />
              PIPELINE RUNNING
            </span>
          )}
        </div>
      </nav>

      {/* ── Body: canvas + drawer ── */}
      <div className="cs-body">
        {/* Left canvas */}
        <div className="cs-canvas">
          <div className="cs-sheet-wrap">
            {imageBlobUrl ? (
              <div className="cs-sheet-container">
                <img
                  ref={sheetRef}
                  src={imageBlobUrl}
                  alt="P&ID drawing"
                  className="cs-sheet"
                  draggable={false}
                />
                <div className="cs-overlay">{renderOverlay()}</div>
              </div>
            ) : (
              <div className="cs-sheet-placeholder">
                <span className="cs-spinner" /> Loading drawing…
              </div>
            )}
          </div>
        </div>

        {/* Right drawer */}
        <aside className="cs-drawer">
          {/* Drawer header */}
          <div className="cs-drawer-header">
            <div className="cs-drawer-header-row">
              <span className="cs-eyebrow">// {isComplete ? 'PIPELINE RUNNING' : 'PIPELINE PAUSED'}</span>
              <span className="cs-step-counter">{step}</span>
            </div>
            <h2 className="cs-headline">{headline}</h2>
            <Stepper view={view} />
            <div className="cs-drawer-divider" />
          </div>

          {/* Drawer body (scrollable) */}
          <div className="cs-drawer-body">
            {renderDrawerBody()}
          </div>
        </aside>
      </div>
    </div>
  );
}
